'use client'

import { Button } from '@/components/ui/button'
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogHeader,
	DialogTitle,
} from '@/components/ui/dialog'
import { PlatformBadge } from '@/components/ui/platform-badge'
import { useDestinationDeployed } from '@/hooks/wallets/use-destination-deployed'
import { useMarketAddress } from '@/hooks/wallets/use-market-address'
import { cn } from '@/lib/utils'
import { useWallet } from '@/providers/WalletContext'
import { useModalStore } from '@/stores/modal-store'
import { ChainType, DisabledUI, HiddenUI, RequiredUI, type WidgetConfig } from '@lifi/widget'
import { EthereumProvider } from '@lifi/widget-provider-ethereum'
import { SolanaProvider } from '@lifi/widget-provider-solana'
import { ArrowLeft } from 'lucide-react'
import { useTranslations } from 'next-intl'
import dynamic from 'next/dynamic'
import { useMemo, useState } from 'react'

// Wallet providers passed to the widget. The widget auto-detects AppKit's
// parent WagmiProvider (supportedChains already covers the LI.FI chain set
// declared in src/config/appkit.ts) and reuses that wagmi instance, so the
// user's source wallet connection is shared instead of duplicated. These
// SDK connectors augment the connector list with MetaMask SDK / Coinbase /
// WalletConnect on top of any EIP-6963 wallets AppKit exposes.
const ethereumProvider = EthereumProvider({
	walletConnect: process.env.NEXT_PUBLIC_WALLETCONNECT_PROJECT_ID
		? { projectId: process.env.NEXT_PUBLIC_WALLETCONNECT_PROJECT_ID }
		: false,
	coinbase: { appName: 'Chance' },
	metaMask: true,
})

// Registers Solana's SDK chain adapter so the widget can interpret Solana
// recipient addresses (used for Kalshi deposits, where source is EVM and
// destination is Solana).
const solanaProvider = SolanaProvider()

const LiFiWidgetClient = dynamic(() => import('./lifi-widget-client'), {
	ssr: false,
	loading: () => (
		<div className="flex h-[640px] items-center justify-center text-brand-gray-secondary text-sm">
			Loading…
		</div>
	),
})

type DepositPlatform = 'polymarket' | 'kalshi' | 'predictfun' | 'myriad' | 'limitless'

type DestinationConfig = {
	chainId: number
	chainType: ChainType
	tokenAddress: string
	tokenLabel: string
}

// Token addresses mirror the per-platform trading clients
// (src/hooks/kalshi/useDflowSwap.ts, src/constants/myriad.ts, src/constants/limitless.ts,
//  src/lib/predictfun/client.ts, and Polymarket's USDC.e on Polygon).
const USDC_E_POLYGON = '0x2791Bca1f2de4661ED88A30C99A7a9449Aa84174'
const USDC_SOL_MINT = 'EPjFWdd5AufqSSqeM2qN1xzybapC8G4wEGGkZwyTDt1v'
const USDT_BSC = '0x55d398326f99059fF775485246999027B3197955'
const USDC_BASE = '0x833589fCD6eDb6E08f4c7C32D4f71b54bdA02913'

const SOLANA_CHAIN_ID = 1151111081099710

const DESTINATIONS: Record<DepositPlatform, DestinationConfig> = {
	polymarket: {
		chainId: 137,
		chainType: ChainType.EVM,
		tokenAddress: USDC_E_POLYGON,
		tokenLabel: 'USDC.e',
	},
	kalshi: {
		chainId: SOLANA_CHAIN_ID,
		chainType: ChainType.SVM,
		tokenAddress: USDC_SOL_MINT,
		tokenLabel: 'USDC',
	},
	predictfun: {
		chainId: 56,
		chainType: ChainType.EVM,
		tokenAddress: USDT_BSC,
		tokenLabel: 'USDT',
	},
	myriad: {
		chainId: 56,
		chainType: ChainType.EVM,
		tokenAddress: USDT_BSC,
		tokenLabel: 'USDT',
	},
	limitless: {
		chainId: 8453,
		chainType: ChainType.EVM,
		tokenAddress: USDC_BASE,
		tokenLabel: 'USDC',
	},
}

const LIFI_API_KEY = process.env.NEXT_PUBLIC_LIFI_API_KEY || undefined

export function LiFiDepositModal() {
	const { activeModal, closeModal, openModal } = useModalStore()
	const isOpen = activeModal === 'lifi-deposit'
	const { eoaAddress, solanaAddress, isPolymarketSafeResolved } = useWallet()
	// The deposit modal only needs the RECIPIENT address; the user's own wallet
	// does the signing. eoaAddress comes from wallet-core and is populated as
	// soon as the embedded-wallet session is ready.
	const receiverAddress = eoaAddress
	const t = useTranslations('depositLifi')

	const [selectedPlatform, setSelectedPlatform] = useState<DepositPlatform | null>(null)

	const resolvedPolymarketAddress = useMarketAddress('polymarket', {
		runtime: receiverAddress ? { eoaAddress: receiverAddress } : undefined,
	})
	// Gate on profile resolution — before it, legacy Safe users would briefly
	// see (and bridge into) the deposit-wallet fallback address instead of
	// their Safe. Undefined disables the Polymarket option until resolved.
	const polymarketSafeAddress = isPolymarketSafeResolved ? resolvedPolymarketAddress : undefined

	// Ground-truth on-chain check: bridging into a contract that has no
	// bytecode lands the funds on a predicted CREATE2 address, where they sit
	// stuck until someone deploys the contract there. Block the widget until
	// we've confirmed the destination is actually a live contract.
	const polymarketDeployment = useDestinationDeployed(
		selectedPlatform === 'polymarket' ? polymarketSafeAddress : undefined
	)

	const recipientFor = (platform: DepositPlatform): string | undefined => {
		switch (platform) {
			case 'polymarket':
				return polymarketSafeAddress
			case 'kalshi':
				return solanaAddress
			case 'predictfun':
			case 'myriad':
			case 'limitless':
				return receiverAddress
			default:
				return undefined
		}
	}

	const availability: Record<DepositPlatform, boolean> = {
		polymarket: !!polymarketSafeAddress,
		kalshi: !!solanaAddress,
		predictfun: !!receiverAddress,
		myriad: !!receiverAddress,
		limitless: !!receiverAddress,
	}

	const hasAnyWallet = !!receiverAddress || !!solanaAddress

	const handleClose = () => {
		setSelectedPlatform(null)
		closeModal()
	}

	const handleBack = () => {
		if (selectedPlatform) {
			setSelectedPlatform(null)
		} else {
			closeModal()
			openModal('deposit-primary')
		}
	}

	const widgetConfig = useMemo<WidgetConfig | null>(() => {
		if (!selectedPlatform) return null
		const dest = DESTINATIONS[selectedPlatform]
		const recipient = recipientFor(selectedPlatform)
		if (!dest || !recipient) return null

		const toAddress = {
			name: t(`platforms.${selectedPlatform}`),
			address: recipient,
			chainType: dest.chainType,
		}

		return {
			integrator: 'chance',
			apiKey: LIFI_API_KEY,
			variant: 'wide',
			toChain: dest.chainId,
			toToken: dest.tokenAddress,
			toAddress,
			toAddresses: [toAddress],
			providers: [ethereumProvider, solanaProvider],
			disabledUI: [DisabledUI.ToToken, DisabledUI.ToAddress],
			requiredUI: [RequiredUI.ToAddress],
			hiddenUI: [
				HiddenUI.Language,
				HiddenUI.Appearance,
				HiddenUI.PoweredBy,
				// Get-gas-on-destination toggle — we sponsor gas for AccountKit
				// operations so offering gas top-up to the user is confusing.
				HiddenUI.GasRefuelMessage,
			],
			appearance: 'dark',
			theme: {
				container: {
					border: '1px solid rgba(255,255,255,0.1)',
					borderRadius: '16px',
					width: '100%',
					// Setting display:flex without an explicit height opts into
					// AppContainer's `maxHeight: 100%` branch (see widget's
					// AppContainer.js). Without this the widget caps itself at
					// ~686px (defaultMaxHeight), so expanded routes/details get
					// clipped inside the modal. flexDirection:column preserves
					// the widget's natural top-down stacking.
					display: 'flex',
					flexDirection: 'column',
				},
				// Right-hand "Receive" panel in wide variant. Applied last inside
				// RoutesExpanded.style.js so it overrides the panel's default
				// `overflow: hidden` + internal maxHeight that otherwise traps
				// route cards behind a scrollbar. With these the panel grows
				// with its content and the outer modal (overflow-y-auto) handles
				// overflow once the viewport is reached.
				routesContainer: {
					height: 'auto',
					maxHeight: 'none',
					overflow: 'visible',
				},
				colorSchemes: {
					dark: {
						palette: {
							primary: { main: '#0a00bd' },
							secondary: { main: '#5edd2d' },
							background: {
								default: '#0b0c0f',
								paper: '#101215',
							},
						},
					},
				},
				shape: {
					borderRadius: 12,
				},
			},
		} satisfies WidgetConfig
	}, [selectedPlatform, receiverAddress, solanaAddress, polymarketSafeAddress, t])

	// Widget view (platform selected, widget config ready)
	if (selectedPlatform && widgetConfig) {
		const polymarketGate =
			selectedPlatform === 'polymarket' && polymarketDeployment.isDeployed !== true
				? polymarketDeployment.isDeployed === false
					? 'undeployed'
					: 'checking'
				: null

		return (
			<Dialog open={isOpen} onOpenChange={open => !open && handleClose()} modal={false}>
				<DialogContent
					closeButtonClasses="size-5 text-white"
					closeButtonContainerClasses="top-2 right-3 cursor-pointer z-10"
					className="max-h-[calc(100dvh-32px)] gap-0 overflow-y-auto overflow-x-hidden rounded-2xl border border-white/10 bg-brand-dark-light p-0 sm:max-w-[960px]"
					onOpenAutoFocus={e => e.preventDefault()}
					onCloseAutoFocus={e => e.preventDefault()}
					onInteractOutside={e => e.preventDefault()}
				>
					<div className="sticky top-0 z-10 flex items-center gap-2 border-white/10 border-b bg-brand-dark-light px-3 py-2">
						<Button variant="ghost" size="icon" className="h-6 w-6" onClick={handleBack}>
							<ArrowLeft className="size-4 text-brand-gray-secondary" />
						</Button>
						<PlatformBadge platform={selectedPlatform} className="size-5" />
						<span className="font-medium text-[13px] text-white">
							{t('depositToWidget', { platform: t(`platforms.${selectedPlatform}`) })}
						</span>
					</div>
					{polymarketGate === 'checking' ? (
						<div className="flex h-[640px] items-center justify-center text-brand-gray-secondary text-sm">
							{t('checkingDestination')}
						</div>
					) : polymarketGate === 'undeployed' ? (
						<div className="flex h-[640px] flex-col items-center justify-center gap-2 px-6 text-center">
							<span className="font-medium text-[14px] text-white">
								{t('destinationNotDeployed')}
							</span>
							<span className="max-w-[360px] text-[12px] text-brand-gray-secondary">
								{t('destinationNotDeployedHint')}
							</span>
						</div>
					) : (
						<div className="p-3">
							<LiFiWidgetClient config={widgetConfig} />
						</div>
					)}
				</DialogContent>
			</Dialog>
		)
	}

	// Platform selection view
	return (
		<Dialog open={isOpen} onOpenChange={open => !open && handleClose()}>
			<DialogContent
				closeButtonClasses="size-[24px] text-white"
				closeButtonContainerClasses="top-[15px] lg:top-[20px] lg:right-[26px] cursor-pointer"
				className="gap-[16px] rounded-3xl border border-white/10 bg-brand-dark-light p-[24px] sm:max-w-[440px]"
			>
				<DialogHeader className="relative gap-[8px]">
					<Button
						variant="ghost"
						size="icon"
						className="-ml-2 -top-[7px] absolute left-0 h-8 w-8"
						onClick={handleBack}
					>
						<ArrowLeft className="size-[24px] text-brand-gray-secondary" />
					</Button>
					<DialogTitle className="text-center font-inter font-semibold text-[18px] text-white leading-[100%]">
						{t('title')}
					</DialogTitle>
					<DialogDescription className="text-center text-[14px] text-brand-gray-secondary leading-[140%]">
						{t('description')}
					</DialogDescription>
				</DialogHeader>

				<div className="flex flex-col gap-[16px]">
					{hasAnyWallet ? (
						<>
							<div className="flex flex-col gap-[8px]">
								<span className="text-[13px] text-brand-gray-secondary">{t('depositTo')}</span>
								<div className="flex flex-col gap-[8px]">
									{(
										[
											'polymarket',
											'kalshi',
											'predictfun',
											'myriad',
											'limitless',
										] as DepositPlatform[]
									).map(platform => {
										const enabled = availability[platform]
										return (
											<button
												key={platform}
												type="button"
												disabled={!enabled}
												onClick={() => enabled && setSelectedPlatform(platform)}
												className={cn(
													'flex items-center gap-3 rounded-[12px] border border-white/10 bg-white/5 p-[14px] text-left transition-colors hover:border-white/20',
													!enabled && 'cursor-not-allowed opacity-50 hover:border-white/10'
												)}
											>
												<PlatformBadge platform={platform} className="size-9" />
												<div className="flex flex-1 flex-col gap-0.5">
													<span className="font-semibold text-[14px] text-white">
														{t(`platforms.${platform}`)}
													</span>
													<span className="text-[12px] text-brand-gray-secondary">
														{t(`platforms.${platform}Description`)}
													</span>
												</div>
												{!enabled && (
													<span className="rounded-full bg-white/10 px-2 py-0.5 text-[10px] text-brand-gray-secondary uppercase tracking-wide">
														{t('comingSoon')}
													</span>
												)}
											</button>
										)
									})}
								</div>
							</div>
							<span className="text-[12px] text-brand-muted">{t('feeDisclaimer')}</span>
						</>
					) : (
						<p className="text-center text-[14px] text-brand-gray-secondary">
							{t('connectWallet')}
						</p>
					)}
				</div>
			</DialogContent>
		</Dialog>
	)
}
